import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  LinearProgress,
} from '@mui/material';
import { api, LeaderboardEntry } from '../services/api';
import HomeIcon from '@mui/icons-material/Home';
import logo from '../assets/images/logo.jpg';
import { useLanguage } from '../translations/LanguageContext';

const LeaderboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { translations } = useLanguage();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const fetchLeaderboard = async () => {
      try {
        setLoading(true);
        const data = await api.getLeaderboard();
        setEntries(data);
      } catch (err) {
        setError(translations.leaderboard.failedToFetch);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [translations.leaderboard.failedToFetch]);

  if (loading) {
    return (
      <Container>
        <Box sx={{ width: '100%', mt: 4 }}>
          <LinearProgress color="primary" />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 6 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 4,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <img 
                src={logo} 
                alt="EcoQuiz Logo" 
                style={{ 
                  width: '60px', 
                  height: 'auto'
                }} 
              />
              <Typography variant="h4" color="primary.main">
                {translations.leaderboard.ecoChampions} (TOP 100)
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={() => navigate('/')}
            >
              {translations.gameEnd.backToHome}
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{translations.leaderboard.rank}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{translations.leaderboard.player}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{translations.leaderboard.score}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{translations.leaderboard.questions}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{translations.leaderboard.time}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((entry, index) => (
                  <TableRow 
                    key={entry.id}
                    sx={{
                      bgcolor: index < 3 ? 'rgba(74, 103, 65, 0.1)' : 'transparent',
                      '&:hover': {
                        bgcolor: 'rgba(74, 103, 65, 0.05)',
                      },
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{entry.name || translations.common.anonymous}</TableCell>
                    <TableCell align="right">{entry.score}</TableCell>
                    <TableCell align="right">{entry.questions_answered}</TableCell>
                    <TableCell align="right">{entry.time}</TableCell>
                  </TableRow>
                ))}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography color="text.secondary">{translations.leaderboard.noEntries}</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </Container>
  );
};

export default LeaderboardPage; 